<template>
  <div class="app-version-view">
    <NavBar />
    <div class="app-version-settings">
      <div class="content-wrapper">
        <!-- Header -->
        <header class="page-header">
          <div class="header-content">
            <div class="header-icon-wrap">
              <v-icon size="28" color="white">mdi-cellphone-cog</v-icon>
            </div>
            <div class="header-text">
              <h1 class="page-title">App Version</h1>
              <p class="page-subtitle">
                Manage version control and update policies for your mobile apps
              </p>
            </div>
            <div class="header-status" :class="{ active: formData.isActive }">
              <span class="status-dot"></span>
              {{ formData.isActive ? 'Live' : 'Disabled' }}
            </div>
          </div>
        </header>

        <!-- Loading -->
        <div v-if="loading" class="loading-container">
          <v-progress-circular indeterminate color="#8B0000" size="56" width="4" />
          <p class="loading-text">Loading settings…</p>
        </div>

        <!-- Alerts -->
        <transition-group name="fade">
          <v-alert
            v-if="error"
            key="error"
            type="error"
            dismissible
            @input="error = null"
            class="alert-modern"
          >
            {{ error }}
          </v-alert>
          <v-alert
            v-if="success"
            key="success"
            type="success"
            dismissible
            @input="success = null"
            class="alert-modern"
          >
            {{ success }}
          </v-alert>
        </transition-group>

        <!-- Settings Display View (Read-Only) -->
        <div v-if="!loading && !editMode">
          <v-card class="settings-card" elevation="0">
            <v-card-text class="card-content">
              <!-- Edit Button -->
              <div class="edit-button-container">
                <v-btn
                  color="#8B0000"
                  dark
                  class="edit-btn"
                  @click="editMode = true"
                  elevation="0"
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  Edit Settings
                </v-btn>
              </div>

              <!-- Version Info Display -->
              <div class="info-display-section">
                <div class="display-header">
                  <v-icon color="#8B0000" size="24">mdi-tag-outline</v-icon>
                  <h3 class="display-title">Version Information</h3>
                </div>
                <div class="display-grid">
                  <div class="display-item">
                    <label class="display-label">Latest Version</label>
                    <p class="display-value">{{ formData.latestVersion || '—' }}</p>
                  </div>
                  <div class="display-item">
                    <label class="display-label">Minimum Supported Version</label>
                    <p class="display-value">{{ formData.minimumSupportedVersion || '—' }}</p>
                  </div>
                </div>
              </div>

              <div class="display-divider"></div>

              <!-- Update Message Display -->
              <div class="info-display-section">
                <div class="display-header">
                  <v-icon color="#8B0000" size="24">mdi-message-text-outline</v-icon>
                  <h3 class="display-title">Update Message</h3>
                </div>
                <div class="display-message-box">
                  <p class="display-message">{{ formData.updateMessage || 'No message set' }}</p>
                </div>
              </div>

              <div class="display-divider"></div>

              <!-- Store URLs Display -->
              <div class="info-display-section">
                <div class="display-header">
                  <v-icon color="#8B0000" size="24">mdi-store-outline</v-icon>
                  <h3 class="display-title">App Store Links</h3>
                </div>
                <div class="display-grid">
                  <div class="display-item">
                    <label class="display-label">
                      <v-icon small color="#3DDC84" class="mr-1">mdi-android</v-icon>
                      Android Store
                    </label>
                    <a v-if="formData.storeUrlAndroid" :href="formData.storeUrlAndroid" target="_blank" class="display-link">
                      {{ formData.storeUrlAndroid }}
                      <v-icon small>mdi-open-in-new</v-icon>
                    </a>
                    <p v-else class="display-value">—</p>
                  </div>
                  <div class="display-item">
                    <label class="display-label">
                      <v-icon small color="#555555" class="mr-1">mdi-apple</v-icon>
                      iOS Store
                    </label>
                    <a v-if="formData.storeUrlIos" :href="formData.storeUrlIos" target="_blank" class="display-link">
                      {{ formData.storeUrlIos }}
                      <v-icon small>mdi-open-in-new</v-icon>
                    </a>
                    <p v-else class="display-value">—</p>
                  </div>
                </div>
              </div>

              <div class="display-divider"></div>

              <!-- Update Policy Display -->
              <div class="info-display-section">
                <div class="display-header">
                  <v-icon color="#8B0000" size="24">mdi-shield-check-outline</v-icon>
                  <h3 class="display-title">Update Policy</h3>
                </div>
                <div class="policy-display-grid">
                  <div class="policy-display-item" :class="{ active: formData.forceUpdate }">
                    <div class="policy-icon">
                      <v-icon size="32" :color="formData.forceUpdate ? '#8B0000' : '#94a3b8'">
                        {{ formData.forceUpdate ? 'mdi-alert-octagon' : 'mdi-information-outline' }}
                      </v-icon>
                    </div>
                    <div class="policy-content">
                      <h4 class="policy-title">Force Update</h4>
                      <p class="policy-status">{{ formData.forceUpdate ? 'Enabled' : 'Disabled' }}</p>
                    </div>
                  </div>
                  <div class="policy-display-item" :class="{ active: formData.isActive }">
                    <div class="policy-icon">
                      <v-icon size="32" :color="formData.isActive ? '#10b981' : '#94a3b8'">
                        {{ formData.isActive ? 'mdi-check-circle' : 'mdi-close-circle' }}
                      </v-icon>
                    </div>
                    <div class="policy-content">
                      <h4 class="policy-title">Version Checking</h4>
                      <p class="policy-status">{{ formData.isActive ? 'Active' : 'Inactive' }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Settings Edit Form -->
        <v-card v-if="!loading && editMode" class="settings-card" elevation="0">
          <v-card-text class="settings-form">
            <v-form ref="form" v-model="valid">
              <!-- Version Information -->
              <section class="form-section">
                <div class="section-header">
                  <div class="section-icon-wrap">
                    <v-icon size="20" color="#8B0000">mdi-tag-outline</v-icon>
                  </div>
                  <div>
                    <h3 class="section-title">Version Information</h3>
                    <p class="section-subtitle">Define the current and minimum supported versions</p>
                  </div>
                </div>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.latestVersion"
                      label="Latest Version"
                      placeholder="e.g., 2.5.0"
                      outlined
                      dense
                      :rules="[rules.required]"
                      prepend-inner-icon="mdi-tag"
                      hint="Current version available in stores"
                      persistent-hint
                      class="modern-field"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.minimumSupportedVersion"
                      label="Minimum Supported Version"
                      placeholder="e.g., 2.0.0"
                      outlined
                      dense
                      prepend-inner-icon="mdi-tag-minus"
                      hint="Oldest version that can still use the app"
                      persistent-hint
                      class="modern-field"
                    />
                  </v-col>
                </v-row>
              </section>

              <div class="section-divider"></div>

              <!-- Update Message -->
              <section class="form-section">
                <div class="section-header">
                  <div class="section-icon-wrap">
                    <v-icon size="20" color="#8B0000">mdi-message-text-outline</v-icon>
                  </div>
                  <div>
                    <h3 class="section-title">Update Message</h3>
                    <p class="section-subtitle">Shown to users when an update is available</p>
                  </div>
                </div>

                <v-textarea
                  v-model="formData.updateMessage"
                  label="Message"
                  placeholder="e.g., New features and bug fixes available! Please update to continue."
                  outlined
                  rows="3"
                  prepend-inner-icon="mdi-message-alert-outline"
                  hint="Keep it short and friendly"
                  persistent-hint
                  counter="200"
                  maxlength="200"
                  class="modern-field"
                />
              </section>

              <div class="section-divider"></div>

              <!-- Store URLs -->
              <section class="form-section">
                <div class="section-header">
                  <div class="section-icon-wrap">
                    <v-icon size="20" color="#8B0000">mdi-store-outline</v-icon>
                  </div>
                  <div>
                    <h3 class="section-title">App Store Links</h3>
                    <p class="section-subtitle">Where users can download the latest version</p>
                  </div>
                </div>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.storeUrlAndroid"
                      label="Android Store URL"
                      placeholder="https://play.google.com/store/apps/details?id=…"
                      outlined
                      dense
                      :rules="[rules.url]"
                      prepend-inner-icon="mdi-android"
                      class="modern-field"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="formData.storeUrlIos"
                      label="iOS Store URL"
                      placeholder="https://apps.apple.com/app/…"
                      outlined
                      dense
                      :rules="[rules.url]"
                      prepend-inner-icon="mdi-apple"
                      class="modern-field"
                    />
                  </v-col>
                </v-row>
              </section>

              <div class="section-divider"></div>

              <!-- Update Policy -->
              <section class="form-section">
                <div class="section-header">
                  <div class="section-icon-wrap">
                    <v-icon size="20" color="#8B0000">mdi-shield-check-outline</v-icon>
                  </div>
                  <div>
                    <h3 class="section-title">Update Policy</h3>
                    <p class="section-subtitle">Control how updates are enforced</p>
                  </div>
                </div>

                <div class="policy-grid">
                  <div class="policy-tile" :class="{ enabled: formData.forceUpdate }">
                    <div class="policy-tile-head">
                      <div class="policy-tile-icon" :class="{ on: formData.forceUpdate }">
                        <v-icon size="20" :color="formData.forceUpdate ? 'white' : '#8B0000'">
                          mdi-alert-octagon-outline
                        </v-icon>
                      </div>
                      <v-switch
                        v-model="formData.forceUpdate"
                        color="#8B0000"
                        hide-details
                        inset
                        class="mt-0"
                      />
                    </div>
                    <h4 class="policy-tile-title">Force Update</h4>
                    <p class="policy-tile-desc">
                      Users on older versions must update before continuing to use the app.
                    </p>
                  </div>

                  <div class="policy-tile" :class="{ enabled: formData.isActive }">
                    <div class="policy-tile-head">
                      <div class="policy-tile-icon" :class="{ on: formData.isActive }">
                        <v-icon size="20" :color="formData.isActive ? 'white' : '#8B0000'">
                          mdi-check-circle-outline
                        </v-icon>
                      </div>
                      <v-switch
                        v-model="formData.isActive"
                        color="#8B0000"
                        hide-details
                        inset
                        class="mt-0"
                      />
                    </div>
                    <h4 class="policy-tile-title">Version Checking</h4>
                    <p class="policy-tile-desc">
                      Enable or disable version checking for all users globally.
                    </p>
                  </div>
                </div>
              </section>

              <!-- Action Buttons -->
              <div class="action-buttons">
                <v-btn
                  large
                  text
                  color="#6b7280"
                  @click="cancelEdit"
                  :disabled="saving"
                  class="action-btn reset-btn"
                >
                  <v-icon left small>mdi-close</v-icon>
                  Cancel
                </v-btn>

                <v-btn
                  large
                  color="#8B0000"
                  dark
                  @click="saveSettings"
                  :loading="saving"
                  :disabled="!valid || saving"
                  class="action-btn save-btn"
                >
                  <v-icon left small>mdi-content-save-outline</v-icon>
                  Save Changes
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Info Cards -->
        <div class="info-cards">
          <div class="info-card">
            <div class="info-card-icon" style="background: linear-gradient(135deg, #8B0000, #5a0000)">
              <v-icon size="22" color="white">mdi-update</v-icon>
            </div>
            <div class="info-card-body">
              <span class="info-card-label">Latest Version</span>
              <span class="info-card-value">{{ formData.latestVersion || '—' }}</span>
            </div>
          </div>

          <div class="info-card">
            <div
              class="info-card-icon"
              :style="{
                background: formData.forceUpdate
                  ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
                  : 'linear-gradient(135deg, #94a3b8, #64748b)',
              }"
            >
              <v-icon size="22" color="white">
                {{ formData.forceUpdate ? 'mdi-alert-circle' : 'mdi-information-outline' }}
              </v-icon>
            </div>
            <div class="info-card-body">
              <span class="info-card-label">Update Type</span>
              <span class="info-card-value">
                {{ formData.forceUpdate ? 'Mandatory' : 'Optional' }}
              </span>
            </div>
          </div>

          <div class="info-card">
            <div
              class="info-card-icon"
              :style="{
                background: formData.isActive
                  ? 'linear-gradient(135deg, #10b981, #059669)'
                  : 'linear-gradient(135deg, #94a3b8, #64748b)',
              }"
            >
              <v-icon size="22" color="white">
                {{ formData.isActive ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
            </div>
            <div class="info-card-body">
              <span class="info-card-label">Status</span>
              <span class="info-card-value">{{ formData.isActive ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar';
import apiService from '@/http/apiService';

export default {
  name: 'AppVersionSettings',
  components: { NavBar },
  data() {
    return {
      valid: false,
      loading: false,
      saving: false,
      error: null,
      success: null,
      editMode: false,
      formData: {
        latestVersion: '',
        minimumSupportedVersion: '',
        updateMessage: '',
        storeUrlAndroid: '',
        storeUrlIos: '',
        forceUpdate: false,
        isActive: true,
      },
      rules: {
        required: (v) => !!v || 'This field is required',
        url: (v) => !v || /^https?:\/\/.+/.test(v) || 'Must be a valid URL',
      },
    };
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      this.error = null;
      try {
        const response = await apiService.getAppVersionSettings();
        if (response.data) {
          this.formData = {
            latestVersion: response.data.latestVersion || '',
            minimumSupportedVersion: response.data.minimumSupportedVersion || '',
            updateMessage: response.data.updateMessage || '',
            storeUrlAndroid: response.data.storeUrlAndroid || '',
            storeUrlIos: response.data.storeUrlIos || '',
            forceUpdate: response.data.forceUpdate || false,
            isActive:
              response.data.isActive !== undefined ? response.data.isActive : true,
          };
        }
      } catch (err) {
        this.error =
          err.response?.data?.message || 'Failed to load settings. Please try again.';
        console.error('Error loading app version settings:', err);
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.error = null;
      this.success = null;
      try {
        await apiService.updateAppVersionSettings(this.formData);
        this.success = 'Settings saved successfully!';
        this.editMode = false;
        setTimeout(() => this.loadSettings(), 1200);
      } catch (err) {
        this.error =
          err.response?.data?.message || 'Failed to save settings. Please try again.';
        console.error('Error saving app version settings:', err);
      } finally {
        this.saving = false;
      }
    },
    cancelEdit() {
      this.editMode = false;
      this.loadSettings();
    },
  },
};
</script>

<style lang="scss" scoped>
$red-primary: #8b0000;
$red-dark: #5a0000;

.app-version-view {
  min-height: 100vh;
}

.app-version-settings {
  min-height: 100vh;
  padding: 12px 0;
}

/* Decorative background blobs */
.bg-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.35;
  pointer-events: none;
  z-index: 0;
}

.blob-1 {
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(139, 0, 0, 0.35), transparent 70%);
  top: -120px;
  right: -80px;
}

.blob-2 {
  width: 380px;
  height: 380px;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.25), transparent 70%);
  bottom: -120px;
  left: -60px;
}

.content-wrapper {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 960px) {
    padding: 0 12px;
  }
}

/* ---------- Header ---------- */
.page-header {
  margin-bottom: 16px;

  .header-content {
    display: flex;
    align-items: center;
    gap: 18px;

    @media (max-width: 600px) {
      flex-wrap: wrap;
    }
  }

  .header-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, $red-primary, $red-dark);
    box-shadow: 0 10px 24px rgba(139, 0, 0, 0.28);
    flex-shrink: 0;
  }

  .header-text {
    flex: 1;
    min-width: 0;
  }

  .page-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.02em;
  }

  .page-subtitle {
    font-size: 0.85rem;
    color: #64748b;
    margin: 2px 0 0;
  }

  .header-status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.15);
    color: #64748b;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #94a3b8;
    }

    &.active {
      background: rgba(16, 185, 129, 0.12);
      color: #059669;

      .status-dot {
        background: #10b981;
        box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.18);
        animation: pulse 2s infinite;
      }
    }
  }
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.18);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.05);
  }
}

/* ---------- Loading ---------- */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 20px;

  .loading-text {
    color: #64748b;
    font-size: 0.95rem;
  }
}

/* ---------- Alerts ---------- */
.alert-modern {
  border-radius: 14px !important;
  margin-bottom: 12px;
}

/* ---------- Settings Card ---------- */
.settings-card {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(14px);
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.06) !important;
  margin-bottom: 16px;
  overflow: hidden;

  .settings-form {
    padding: 16px;

    @media (max-width: 960px) {
      padding: 12px;
    }
  }
}

/* ---------- Display View Styles ---------- */
.edit-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;

  .edit-btn {
    border-radius: 12px !important;
    font-weight: 600 !important;
    text-transform: none !important;
    letter-spacing: 0.5px;
    background: linear-gradient(135deg, $red-primary, $red-dark) !important;
    box-shadow: 0 10px 24px rgba(139, 0, 0, 0.28) !important;
    transition: all 0.25s ease;

    &:hover {
      box-shadow: 0 12px 28px rgba(139, 0, 0, 0.35) !important;
      transform: translateY(-2px);
    }
  }
}

.info-display-section {
  margin-bottom: 24px;

  .display-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    .display-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #0f172a;
      margin: 0;
    }
  }

  .display-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
  }

  .display-item {
    .display-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
    }

    .display-value {
      font-size: 1.1rem;
      font-weight: 600;
      color: #0f172a;
      margin: 0;
    }

    .display-link {
      font-size: 0.9rem;
      color: $red-primary;
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 4px;
      transition: all 0.2s;

      &:hover {
        color: $red-dark;
        text-decoration: underline;
      }
    }
  }

  .display-message-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;

    .display-message {
      font-size: 0.95rem;
      color: #475569;
      margin: 0;
      line-height: 1.6;
    }
  }

  .policy-display-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }

  .policy-display-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    transition: all 0.25s ease;

    &.active {
      background: linear-gradient(135deg, rgba(139, 0, 0, 0.04), rgba(139, 0, 0, 0.01));
      border-color: rgba(139, 0, 0, 0.2);
      box-shadow: 0 4px 12px rgba(139, 0, 0, 0.08);
    }

    .policy-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .policy-content {
      flex: 1;

      .policy-title {
        font-size: 0.95rem;
        font-weight: 600;
        color: #0f172a;
        margin: 0 0 4px 0;
      }

      .policy-status {
        font-size: 0.85rem;
        color: #64748b;
        margin: 0;
      }
    }
  }
}

.display-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 24px 0;
}

/* ---------- Sections ---------- */
.form-section {
  margin-bottom: 4px;
}

.section-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;

  .section-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 11px;
    background: linear-gradient(135deg, rgba(139, 0, 0, 0.1), rgba(139, 0, 0, 0.04));
    flex-shrink: 0;
  }

  .section-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.01em;
  }

  .section-subtitle {
    font-size: 0.825rem;
    color: #64748b;
    margin: 2px 0 0;
  }
}

.section-divider {
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(139, 0, 0, 0.12),
    transparent
  );
  margin: 12px 0;
}

/* ---------- Policy tiles ---------- */
.policy-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
}

.policy-tile {
  padding: 14px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  transition: all 0.25s ease;

  &.enabled {
    background: linear-gradient(135deg, rgba(139, 0, 0, 0.04), rgba(139, 0, 0, 0.01));
    border-color: rgba(139, 0, 0, 0.35);
    box-shadow: 0 6px 20px rgba(139, 0, 0, 0.08);
  }

  .policy-tile-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .policy-tile-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: rgba(139, 0, 0, 0.1);
    transition: all 0.25s ease;

    &.on {
      background: linear-gradient(135deg, $red-primary, $red-dark);
      box-shadow: 0 6px 16px rgba(139, 0, 0, 0.3);
    }
  }

  .policy-tile-title {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px;
  }

  .policy-tile-desc {
    font-size: 0.83rem;
    color: #64748b;
    line-height: 1.5;
    margin: 0;
  }
}

/* ---------- Actions ---------- */
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eef2f7;

  @media (max-width: 600px) {
    flex-direction: column-reverse;

    .action-btn {
      width: 100%;
    }
  }

  .action-btn {
    min-width: 150px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0.01em;
    border-radius: 12px !important;
  }

  .reset-btn {
    color: #64748b !important;
  }

  .save-btn {
    background: linear-gradient(135deg, $red-primary, $red-dark) !important;
    box-shadow: 0 10px 24px rgba(139, 0, 0, 0.28) !important;
    transition: all 0.25s ease;

    &:hover {
      box-shadow: 0 14px 32px rgba(139, 0, 0, 0.4) !important;
      transform: translateY(-2px);
    }
  }
}

/* ---------- Info cards ---------- */
.info-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.info-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.08);
  }

  .info-card-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 14px;
    flex-shrink: 0;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
  }

  .info-card-body {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .info-card-label {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
  }

  .info-card-value {
    font-size: 1.15rem;
    font-weight: 700;
    color: #0f172a;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* ---------- Vuetify field polish ---------- */
::v-deep .modern-field {
  .v-input__slot {
    border-radius: 12px !important;
    background: #fbfcfe !important;
  }

  fieldset {
    border-color: #e2e8f0 !important;
  }

  &.v-input--is-focused fieldset {
    border-color: $red-primary !important;
    border-width: 2px !important;
  }

  .v-label {
    font-size: 0.9rem;
  }
}

/* ---------- Transitions ---------- */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>